/**
 * Cornerstone landing pages — all user-visible strings (NL source).
 *
 * Icons are referenced by string name → resolved to lucide-react components
 * in the page files. `recommend()` thresholds stay in JSX (language-agnostic);
 * only the title/body/cta strings are translated.
 *
 * Translated copies live in cornerstones/{lang}.js, generated by
 * /app/backend/scripts/translate_cornerstones.py.
 */
export const CORNERSTONES_NL = {
  beste: {
    seo: {
      title: "Paardenbeheer app: beste keuze voor 2026 | EquiSight",
      description:
        "Vergelijk paardenbeheer apps voor eigenaar, stal en fokkerij. Ontdek waarom EquiSight de complete app is voor jouw paard.",
      keywords: [
        "paardenbeheer app",
        "stalmanagement app",
        "paarden logboek app",
        "app voor meerdere paarden beheren",
        "merrie drachtigheid app",
        "fokkerij app paarden",
        "beste paardenbeheer app",
      ],
    },
    hero: {
      eyebrow: "Keuzehulp · paardenbeheer software",
      breadcrumbLabel: "Paardenbeheer app",
      title: "Paardenbeheer app: de complete keuzehulp voor eigenaren, stallen en fokkerijen.",
      intro:
        "Niet zomaar een lijst features — een eerlijke vergelijking, een keuzehulp en een blik op wat een paardenbeheer app écht moet kunnen in 2026. Voor één paard, voor een hele stal, of voor jouw fokkerij.",
      pills: [
        { icon: "Users", label: "Voor eigenaren" },
        { icon: "Building2", label: "Voor stallen" },
        { icon: "Baby", label: "Voor fokkerijen" },
        { icon: "Share2", label: "Voor teams" },
      ],
      ctas: [
        { label: "Start gratis met 1 paard", href: "#start", primary: true },
        { label: "Naar vergelijkingstabel", href: "#vergelijking" },
      ],
    },
    pains: {
      title: "Waarom een paardenbeheer app steeds belangrijker wordt.",
      lead:
        "Paardenbeheer is in de praktijk versnipperd: WhatsApp, agenda, Excel, foto's op je telefoon, een papieren paspoort, losse facturen en mondelinge afspraken. Bij één paard al onhandig. Bij meerdere paarden, fokkerijen of gedeelde zorg ontstaan er gaten, gemiste afspraken en discussies.",
      items: [
        "Waar stond die vaccinatiedatum ook alweer?",
        "Wie heeft de hoefsmid betaald?",
        "Welke merrie moet vandaag gecontroleerd worden?",
        "Heeft de trainer de instructie van vorige week wel gelezen?",
        "Heb ik dat foto-document echt opgeslagen?",
        "Klopt het voerschema dat in de stal hangt nog?",
      ],
    },
    what: {
      title: "Wat is een paardenbeheer app?",
      lead:
        "Een paardenbeheer app is in essentie een digitaal dossier per paard. Geen versnipperde notities, maar één plek voor gezondheid, training, voeding, documenten, planning, herinneringen, kosten, foto's, video's en gedeelde toegang. Iedereen rondom het paard kijkt naar dezelfde, actuele informatie.",
      targetGroupsTitle: "Voor wie is een paardenbeheer app bedoeld?",
      targetGroups: [
        { icon: "Heart", title: "Particuliere eigenaar", body: "Eén paard, één dossier — gezondheid, planning en historie in je broekzak." },
        { icon: "Users", title: "Eigenaar met meerdere paarden", body: "Aparte profielen, aparte planning, gedeelde takenlijst voor het hele team." },
        { icon: "Building2", title: "Pensionstal & manege", body: "Eigenaarstoegang, verzorgingsinstructies, gedeelde agenda en taken." },
        { icon: "Baby", title: "Fokkerij", body: "Merriecyclus, dracht, hengstgegevens, stamboom en veulenontwikkeling." },
        { icon: "Target", title: "Trainers", body: "Inzicht per paard, sessies loggen, doelen stellen, video's analyseren." },
        { icon: "ClipboardList", title: "Professionals", body: "Hoefsmid, tandarts, fysio of dierenarts — werken vanuit hetzelfde dossier." },
      ],
    },
    criteria: {
      title: "Waar moet een goede paardenbeheer app aan voldoen?",
      lead:
        "Een app is niet alleen mooi — hij moet dagelijks praktisch werken. Vijf onderdelen die niet mogen ontbreken in de keuze van 2026.",
      items: [
        { icon: "FileText", title: "Paardendossier per paard", body: "Gegevens, leeftijd, ras, paspoortinformatie, eigenaar, stal, foto's en documenten — allemaal gekoppeld aan één paardprofiel." },
        { icon: "Heart", title: "Gezondheid en verzorging", body: "Vaccinaties, ontworming, hoefsmid, tandarts, dierenarts, behandelingen, medicatie en automatische herinneringen vóór de vervaldatum." },
        { icon: "Activity", title: "Training en voortgang", body: "Trainingen loggen, notities, video's, wedstrijdresultaten, doelen en voortgang per discipline of per paard." },
        { icon: "Apple", title: "Voeding en management", body: "Voerschema's, supplementen, ruwvoer, gewicht, lichaamsconditie en de dagelijkse routine die de stal moet kennen." },
        { icon: "Share2", title: "Delen met anderen", body: "Toegang voor eigenaar, verzorger, trainer, stalhouder of professional — met rechtenbeheer dat past bij ieders rol." },
        { icon: "Calendar", title: "Planning en herinneringen", body: "Een gedeelde agenda met taken, automatische herinneringen en bevestiging zodat niets glipt door de mazen." },
      ],
    },
    comparison: {
      title: "EquiSight vs andere paardenbeheer apps.",
      lead:
        "De Nederlandse SERP wordt momenteel gevuld door Stalmanager, LogMyHorse, EquineM en Happie Horse. Ze hebben elk hun sterke punten. We zetten de meest gevraagde functies naast elkaar, eerlijk en op basis van publiek beschikbare informatie.",
      competitors: ["EquiSight", "Stalmanager", "LogMyHorse", "EquineM", "Happie Horse"],
      rows: [
        { label: "Gratis starten",            values: [true,  true,  true,  false, true]  },
        { label: "Meerdere paarden",          values: [true,  true,  true,  true,  true]  },
        { label: "Gezondheidsdossier",        values: [true,  true,  true,  true,  true]  },
        { label: "Training & voortgang",      values: [true,  false, true,  true,  true]  },
        { label: "Voeding & supplementen",    values: [true,  false, false, true,  false] },
        { label: "Planning & herinneringen",  values: [true,  true,  true,  true,  true]  },
        { label: "Documenten per paard",      values: [true,  true,  true,  true,  true]  },
        { label: "Delen met team & rechten",  values: [true,  true,  false, true,  false] },
        { label: "Fokkerijmodule (compleet)", values: [true,  false, false, false, false] },
        { label: "Merriecyclus",              values: [true,  false, false, false, false] },
        { label: "Drachtplanning + echo's",   values: [true,  false, false, false, false] },
        { label: "Veulenontwikkeling",        values: [true,  false, false, false, false] },
        { label: "Professionals integreren",  values: [true,  false, false, true,  false] },
        { label: "EquiCoach inzichten",       values: [true,  false, false, false, false] },
        { label: "Geschikt voor eigenaar",    values: [true,  true,  true,  true,  true]  },
        { label: "Geschikt voor fokkerij",    values: [true,  false, false, false, false] },
        { label: "Geschikt voor stal/manege", values: [true,  true,  false, true,  false] },
      ],
      note: "Vergelijking op basis van publiek beschikbare features in januari 2026. Concurrenten zijn ook goede tools; EquiSight is gebouwd om alle modules in één app te combineren.",
    },
    why: {
      title: "Waarom EquiSight anders is.",
      lead:
        "Een logboek registreert. EquiSight combineert paardenbeheer, training, voeding, gezondheid, wedstrijden, professionals en fokkerij in één moderne app — en gaat een stap verder met inzichten.",
      points: [
        { icon: "Heart", title: "Eén app voor het hele leven van het paard", body: "Van veulen tot sportpaard, fokmerrie of pensioenpaard. Het dossier groeit met je paard mee — niets verdwijnt onderweg." },
        { icon: "Sparkles", title: "Niet alleen data, ook patronen", body: "EquiCoach combineert ontwikkeling, terugkerende klachten, training en voeding — zodat je tijdig signalen ziet, geen losse meldingen." },
        { icon: "Share2", title: "Gebouwd voor samenwerking", body: "Eigenaar, stalhouder, trainer, hoefsmid en andere professionals werken vanuit hetzelfde paardendossier — met de rechten die bij hun rol passen." },
      ],
    },
    nicheA: {
      eyebrow: "Niche · fokkerij",
      title: "Paardenbeheer voor fokkerijen.",
      lead:
        "Veel concurrenten noemen fokkerij in één regel. EquiSight bouwde een complete fokkerijmodule — omdat de fokker zelden de eindgebruiker is van een algemene app, maar de planning juist meer dan iemand anders nodig heeft.",
      items: [
        "Merriecyclus dagelijks bijhouden",
        "Inseminatiedatum + hengstgegevens",
        "Drachtcontrole met echo's",
        "Verwachte veulendatum + waarschuwingen",
        "Veulenontwikkeling per maand",
        "Stamboom en afstamming",
        "Notities per merrie",
        "Overzicht van alle drachtige merries",
      ],
      ctaLabel: "Bekijk de fokkerijmodule",
      ctaHref: "/fokkerij",
    },
    nicheB: {
      eyebrow: "Niche · stal & manege",
      title: "Paardenbeheer voor meerdere paarden en pensionstallen.",
      lead:
        "Bij meerdere paarden draait het minder om data, en meer om communicatie. Iedere eigenaar heeft eigen wensen, routines, voerafspraken en medische aandachtspunten. Iedere medewerker moet daar zonder zoeken bij kunnen.",
      cards: [
        { t: "Paarden per eigenaar", d: "Iedere eigenaar ziet alleen zijn paard(en)." },
        { t: "Verzorgingsinstructies", d: "Per paard zichtbaar voor de stal." },
        { t: "Gedeelde agenda", d: "Hoefsmid, dierenarts en wedstrijden in één view." },
        { t: "Takenlijst", d: "Toewijzen aan medewerkers, met bevestiging." },
        { t: "Eigenaarstoegang", d: "Klanten zien hun paard in eigen app." },
        { t: "Noodcontacten", d: "Altijd vindbaar bij stress in de stal." },
      ],
      ctaLabel: "Bekijk de manegemodule",
      ctaHref: "/manege-module",
    },
    beforeAfter: {
      title: "Van WhatsApp, Excel en whiteboard naar EquiSight.",
      lead: "Concreet: zo verandert je dagelijks paardenbeheer.",
      rows: [
        { before: "WhatsApp groepjes", after: "Gestructureerde notities per paard" },
        { before: "Excel sheets", after: "Live paardendossier met historie" },
        { before: "Whiteboard in de stal", after: "Gedeelde planning op je telefoon" },
        { before: "Losse foto's op camera", after: "Media gekoppeld aan je paard" },
        { before: "Mondelinge afspraken", after: "Taken en reminders met bevestiging" },
        { before: "Papieren paspoort kwijt", after: "Documenten altijd bij de hand" },
      ],
    },
    helper: {
      title: "Welke paardenbeheer app past bij jou?",
      lead: "Beantwoord deze 5 vragen en je krijgt een eerlijke aanbeveling — ook als die niet altijd EquiSight is.",
      questions: [
        {
          id: "horses",
          q: "Heb je één paard of meerdere paarden?",
          options: [
            { label: "Eén paard", score: 1 },
            { label: "2–5 paarden", score: 2 },
            { label: "Meer dan 5 paarden", score: 3 },
          ],
        },
        {
          id: "clients",
          q: "Beheer je paarden van klanten of derden?",
          options: [
            { label: "Nee, alleen mijn eigen paarden", score: 0 },
            { label: "Soms — voor familie of bekenden", score: 1 },
            { label: "Ja, professioneel (stal / manege)", score: 3 },
          ],
        },
        {
          id: "breeding",
          q: "Wil je fokkerij en dracht bijhouden?",
          options: [
            { label: "Niet relevant", score: 0 },
            { label: "Soms — incidentele fok", score: 1 },
            { label: "Ja, ik ben (of word) fokker", score: 3 },
          ],
        },
        {
          id: "collab",
          q: "Werk je samen met trainer, verzorger of professional?",
          options: [
            { label: "Nee, ik doe alles zelf", score: 0 },
            { label: "Ja, met 1 persoon", score: 1 },
            { label: "Ja, met meerdere personen", score: 3 },
          ],
        },
        {
          id: "depth",
          q: "Wil je alleen registreren of ook inzicht krijgen?",
          options: [
            { label: "Alleen registreren", score: 1 },
            { label: "Registreren + planning", score: 2 },
            { label: "Registreren + planning + inzichten", score: 3 },
          ],
        },
      ],
      results: {
        low: {
          title: "Een simpel logboek volstaat — én groeit met je mee.",
          body: "Voor één paard zonder gedeelde zorg is iedere paardenbeheer app geschikt. EquiSight start gratis met 1 paard, dus je hebt geen risico. Mocht je later meer paarden, een trainer of fokkerij willen toevoegen, dan zit alles er al in.",
          cta: "Start gratis met 1 paard",
        },
        mid: {
          title: "EquiSight past goed bij jouw situatie.",
          body: "Je hebt meerdere behoeftes (meerdere paarden, samenwerking of inzicht). De meeste concurrenten dekken 1-2 modules; EquiSight combineert ze in één app, zonder dat je losse tools koppelt.",
          cta: "Bekijk wat EquiSight kan",
        },
        high: {
          title: "EquiSight is de meest logische keuze.",
          body: "Je situatie combineert meerdere paarden, samenwerking, fokkerij en/of inzichten. Dit is precies waar EquiSight het verschil maakt — geen versnipperde tools, maar één paardendossier voor alles en iedereen rondom het paard.",
          cta: "Start gratis met EquiSight",
        },
      },
    },
    faqTitle: "Veelgestelde vragen over paardenbeheer apps",
    faq: [
      { q: "Wat is de beste paardenbeheer app?", a: "De beste paardenbeheer app hangt af van je situatie. Voor alleen een simpel logboek zijn meerdere apps geschikt. Wil je gezondheid, training, voeding, documenten, fokkerij, planning en samenwerking combineren, dan is EquiSight gebouwd als complete oplossing waarbij alles in één dossier samenkomt." },
      { q: "Kan ik meerdere paarden beheren in één app?", a: "Ja. Een goede paardenbeheer app moet meerdere paarden ondersteunen, inclusief eigenaren, verzorgers, routines, documenten, afspraken en herinneringen per paard. EquiSight is hier vanaf de eerste regel code op ontworpen — paarden zijn individuele dossiers, geen rijen in een lijst." },
      { q: "Is EquiSight ook geschikt voor fokkerijen?", a: "Ja. EquiSight kan worden ingericht met fokkerijfuncties zoals merriecyclus, inseminatiedatum, drachtcontrole, hengstgegevens, stamboom, verwachte veulendatum en veulenontwikkeling. Dit is precies het gebied waar de meeste concurrerende apps niet of nauwelijks op inspelen." },
      { q: "Kan ik gegevens delen met mijn trainer of hoefsmid?", a: "Ja. Het doel van EquiSight is dat relevante informatie veilig gedeeld kan worden met mensen rondom het paard — trainer, hoefsmid, tandarts, dierenarts, fysio, verzorger of eigenaar. Iedere persoon krijgt alleen de rechten die nodig zijn voor zijn rol." },
      { q: "Waarom niet gewoon WhatsApp of Excel gebruiken?", a: "WhatsApp en Excel werken zolang het eenvoudig blijft. Zodra je meerdere paarden, afspraken, documenten, behandelingen, eigenaren of fokkerijdata hebt, wordt het foutgevoelig: informatie verspreidt zich, terugzoeken kost tijd en mondelinge afspraken verdwijnen. Een paardenbeheer app brengt alles samen per paard, met automatische herinneringen." },
      { q: "Wat kost EquiSight?", a: "Je kunt gratis starten met 1 paard. Wil je meerdere paarden beheren, fokkerij activeren of professionals toegang geven, dan bestaan er betaalbare Premium-plannen. Bekijk de actuele opties op de prijzenpagina." },
    ],
  },

  fokkerij: {
    seo: {
      title: "Fokkerij software paard: complete vergelijking 2026",
      description:
        "Vergelijk fokkerij software voor paarden: merriecyclus, dracht, hengstkeuze en veulens. Ontdek waarom EquiSight de complete fokkerij app is.",
      keywords: [
        "fokkerij software paard",
        "merriecyclus app",
        "merrie drachtigheid app",
        "veulen registratie app",
        "stamboek app paard",
        "dekplanning paard",
        "hengstkeuze app",
      ],
    },
    hero: {
      eyebrow: "Keuzehulp · fokkerij software",
      breadcrumbLabel: "Fokkerij software",
      title: "Fokkerij software voor paarden: de complete vergelijking voor 2026.",
      intro:
        "Een fokmerrie verdient meer dan een algemene paardenbeheer app. Vergelijk de fokkerij-functies van de bekendste tools, ontdek waarop je écht moet letten, en kies bewust voor de software die past bij jouw stoeterij — groot of klein.",
      pills: [
        { icon: "Baby", label: "Merrie cyclus" },
        { icon: "Heart", label: "Drachtcontrole" },
        { icon: "Sparkles", label: "Veulenontwikkeling" },
        { icon: "FileText", label: "Stamboek" },
      ],
      ctas: [
        { label: "Start gratis met de fokkerijmodule", href: "#start", primary: true },
        { label: "Naar vergelijkingstabel", href: "#vergelijking" },
      ],
    },
    pains: {
      title: "Waarom een gewone paardenbeheer app niet volstaat voor fokkerij.",
      lead:
        "De meeste paardensport apps zijn ontworpen voor één sport- of recreatiepaard. Een fokkerij heeft een hele andere ritmiek: hengstigheid, inseminatie, drachtcontrole, echo's, veulenkalender, stamboom en verkoop. Eén Excel raakt na 2 seizoenen uit het lood — en bij meerdere merries verlies je grip.",
      items: [
        "Wanneer was die laatste cyclus van Bonita ook alweer?",
        "Welke hengst staat gepland voor de dekking volgend seizoen?",
        "Is de 21-daagse echo al ingepland?",
        "Welke veulens van 2024 zijn al verkocht?",
        "Waar staat het stamboomdiagram tot opa van moederszijde?",
        "Waar zijn de DNA-monsters en paspoortgegevens?",
      ],
    },
    what: {
      title: "Wat is fokkerij software voor paarden?",
      lead:
        "Fokkerij software is een gespecialiseerd dossier per merrie en hengst, gecombineerd met een fokjaar-kalender, drachtplanning, veulenregistratie en stamboomweergave. Geen algemeen paardenlogboek, maar een tool die de fokcyclus begrijpt: van eerste hengstigheid tot afspenen.",
      targetGroupsTitle: "Voor welk type fokker is fokkerij software bedoeld?",
      targetGroups: [
        { icon: "Baby", title: "Hobbyfokker (1-3 merries)", body: "Eén of twee dekkingen per jaar — overzicht zonder Excel-acrobatiek." },
        { icon: "Building2", title: "Kleine stoeterij (4-10 merries)", body: "Een fokjaarplan met cyclus, inseminatie, drachtcontroles en veulendossier." },
        { icon: "Users", title: "Professionele stoeterij (10+)", body: "Meerdere medewerkers, hengstkeuze per merrie, verkoop en marketing." },
        { icon: "Target", title: "Hengstenhouder", body: "Bestelhistorie, sperma-statistieken en agenda per hengst." },
        { icon: "Heart", title: "Embryotransplantatie", body: "Donor + ontvangermerrie als gekoppeld dossier, ET-kalender." },
        { icon: "ClipboardList", title: "Stamboek registratie", body: "DNA-monsters, paspoortdocumenten en stamboomgegevens." },
      ],
    },
    criteria: {
      title: "Waar moet goede fokkerij software aan voldoen?",
      lead:
        "Niet elke 'fokkerijfunctie' in een paardenapp is hetzelfde. Vijf kerngebieden waar fokkers veel tijd verliezen — en waar software écht verschil maakt.",
      items: [
        { icon: "Calendar", title: "Merriecyclus & hengstigheid", body: "Dagelijkse log, voorspellende ovulatie, voortrolling per cyclus en automatische herinneringen." },
        { icon: "Heart", title: "Inseminatie & drachtcontrole", body: "Dek- en inseminatiedata, hengstgegevens, 14-daagse echo, 21-daagse echo, 60-daagse echo en geslachtsbepaling." },
        { icon: "Baby", title: "Veulenontwikkeling", body: "Geboorte gegevens, gewichtsgrafiek, ontwormingsschema, speenmoment, paspoortregistratie." },
        { icon: "FileText", title: "Stamboom & papieren", body: "Stamboomweergave tot 4-5 generaties, DNA, paspoortnummers en stamboekstatus." },
        { icon: "Target", title: "Hengstkeuze & matching", body: "Filtreren op discipline, lijnen, hengstgegevens en historisch succes — niet alleen wat een hengsthouder pusht." },
        { icon: "Share2", title: "Samenwerking dierenarts/inseminator", body: "Echo's, monsters en statussen direct gedeeld met de praktijk die werkt aan jouw merries." },
      ],
    },
    comparison: {
      title: "EquiSight vs andere fokkerij software.",
      lead:
        "De Nederlandse SERP voor fokkerij software toont vaak generieke paardenapps die fokkerij in één zin noemen. We vergelijken eerlijk welke tools écht een complete fokkerijflow hebben.",
      competitors: ["EquiSight", "Stalmanager", "HorseManager", "EquineGenie", "Foal-Excel"],
      rows: [
        { label: "Gratis starten",                  values: [true,  true,  false, false, true]  },
        { label: "Merriecyclus loggen",             values: [true,  false, true,  true,  true]  },
        { label: "Inseminatiedatum + hengst",       values: [true,  false, true,  true,  true]  },
        { label: "Echo's & drachtcontrole",         values: [true,  false, true,  true,  false] },
        { label: "Verwachte veulendatum",           values: [true,  false, true,  true,  true]  },
        { label: "Veulen dossier (gewicht/groei)",  values: [true,  false, true,  true,  false] },
        { label: "Stamboom tot 4-5 generaties",     values: [true,  false, false, true,  false] },
        { label: "DNA & paspoortregistratie",       values: [true,  false, false, true,  false] },
        { label: "Hengstkeuze / matching",          values: [true,  false, false, true,  false] },
        { label: "ET / embryotransplantatie",       values: [true,  false, false, true,  false] },
        { label: "Multi-merrie overzicht",          values: [true,  true,  true,  true,  true]  },
        { label: "Mobiel (iOS/Android)",            values: [true,  true,  false, false, false] },
        { label: "Delen met dierenarts",            values: [true,  false, false, true,  false] },
        { label: "Geïntegreerd met algemeen dossier", values: [true, true,  false, false, false] },
        { label: "Verkoop & marketing van veulens", values: [true,  false, false, true,  false] },
      ],
      note: "Vergelijking op basis van publiek beschikbare features in januari 2026. EquineGenie is sterk in fokkerij, maar minder in mobiel + algemeen paardenbeheer. EquiSight combineert beide.",
    },
    why: {
      title: "Waarom EquiSight fokkers anders bedient.",
      lead:
        "Veel fokkers werken met losse fokkerij software én een aparte paardenmanagement app. EquiSight combineert beide — zonder dat je tussen apps switcht of data dubbel invoert.",
      points: [
        { icon: "Heart", title: "Fokmerrie blijft ook gewoon paard", body: "De fokmerrie heeft een fokjaar én een gewoon paardendossier — gezondheid, vaccinaties, hoefsmid. Eén app, alle modules." },
        { icon: "Sparkles", title: "Voorspellend ipv reactief", body: "Cyclusvoorspelling, drachtmijlpaal-herinneringen en veulenkalender — automatisch op basis van jouw data." },
        { icon: "Share2", title: "Iedereen rondom de fokkerij", body: "Inseminator, hengstenhouder, dierenarts, koper, stamboek — allemaal toegang met de juiste rechten." },
      ],
    },
    nicheA: {
      eyebrow: "Per discipline · cyclus tot afspenen",
      title: "Wat een fokkerijdossier in EquiSight bevat.",
      lead: "Per merrie krijg je een compleet fokdossier, gekoppeld aan haar reguliere paarddossier.",
      items: [
        "Dagelijkse cyclus log met voorspelling",
        "Inseminatie/dekking + hengstgegevens",
        "Echo timeline (14/21/60 dagen + geslacht)",
        "Drachtmijlpalen met automatische reminders",
        "Verwachte veulendatum en voorbereiding",
        "Veulen dossier vanaf geboorte",
        "Stamboom tot opa/oma met klik-navigatie",
        "DNA-monster, paspoort, stamboekstatus",
        "Verkoop pipeline met geïnteresseerden",
        "Hengstkeuze geschiedenis per merrie",
      ],
      ctaLabel: "Bekijk de fokkerijmodule",
      ctaHref: "/fokkerij",
    },
    nicheB: {
      eyebrow: "Voor de hele stoeterij",
      title: "Overzicht voor meerdere merries en hengsten.",
      lead: "Wanneer je meer dan 2-3 merries fokt, wordt overzicht over alle cycli en drachten cruciaal.",
      cards: [
        { t: "Cyclus dashboard", d: "Alle merries naast elkaar, met cyclusstadium en volgende actie." },
        { t: "Dracht pipeline", d: "Welke merries zijn waar in de drachtmijlpalen?" },
        { t: "Veulenkalender", d: "Verwachte geboortes per maand, met logistiek erop afgestemd." },
        { t: "Hengst portfolio", d: "Eigen hengsten of vaste inseminatoren met bestelhistorie." },
        { t: "Stamboomgrafiek", d: "Voorouder analyse over meerdere generaties en lijnen." },
        { t: "Verkoop dashboard", d: "Welke veulens zijn verkocht, gereserveerd of nog beschikbaar." },
      ],
    },
    beforeAfter: {
      title: "Van Excel + agenda naar EquiSight fokkerij.",
      lead: "Concreet: zo verandert je fokseizoen.",
      rows: [
        { before: "Cyclus in losse notitie", after: "Dagelijkse log met voorspelling" },
        { before: "Echo's via WhatsApp aan inseminator", after: "Gedeeld dossier met fotos" },
        { before: "Drachtdatum in agenda", after: "Automatische mijlpaal reminders" },
        { before: "Veulenpaspoort in la", after: "Document gekoppeld aan veulen profiel" },
        { before: "Stamboom op papier", after: "Klikbare stamboom tot 4-5 generaties" },
        { before: "Verkoopadvertentie op Marktplaats", after: "Verkoop pipeline met interesselijst" },
      ],
    },
    helper: {
      title: "Welke fokkerij software past bij jou?",
      lead: "5 vragen om te kijken of je een complete fokkerij app nodig hebt of een hobby-tool volstaat.",
      questions: [
        { id: "mares", q: "Hoeveel fokmerries beheer je?", options: [
          { label: "1 merrie", score: 1 },
          { label: "2-5 merries", score: 2 },
          { label: "Meer dan 5 merries", score: 3 },
        ] },
        { id: "frequency", q: "Hoe vaak fok je per jaar?", options: [
          { label: "Incidenteel (om de paar jaar)", score: 0 },
          { label: "Elk jaar 1-2 dekkingen", score: 2 },
          { label: "Elk jaar 3+ dekkingen", score: 3 },
        ] },
        { id: "method", q: "Hoe insemineer/dek je?", options: [
          { label: "Natuurlijke dekking", score: 1 },
          { label: "KI vers/gekoeld sperma", score: 2 },
          { label: "ET of diepvries", score: 3 },
        ] },
        { id: "team", q: "Werk je samen met dierenarts/inseminator?", options: [
          { label: "Eén vaste persoon", score: 1 },
          { label: "Meerdere praktijken", score: 2 },
          { label: "Internationaal netwerk", score: 3 },
        ] },
        { id: "tracking", q: "Wat houd je nu bij?", options: [
          { label: "Excel of papier", score: 1 },
          { label: "Algemene paardenbeheer app", score: 2 },
          { label: "Niets gestructureerd", score: 3 },
        ] },
      ],
      results: {
        low: { title: "Een hobby-tool of Excel volstaat — maar EquiSight blijft handig.", body: "Voor 1 merrie met natuurlijke dekking om de paar jaar is een uitgebreide fokkerijmodule overkill. EquiSight start gratis en activeer je de fokkerijmodule alleen voor het jaar dat je dekt — zonder upgrade verplichting.", cta: "Start gratis" },
        mid: { title: "EquiSight fokkerij is de juiste tool.", body: "Je hebt meerdere merries of meerdere fokmethodes — daar zijn algemene paardenapps niet op gebouwd. EquiSight combineert fokkerij + algemene zorg, zodat je niet tussen twee tools schakelt.", cta: "Bekijk de fokkerijmodule" },
        high: { title: "EquiSight is gebouwd voor jouw stoeterij.", body: "Meerdere merries, ET of diepvries, internationaal team — de meeste tools komen hier tekort. EquiSight schaalt mee tot stoeterij-niveau zonder dat je extra licenties stapelt.", cta: "Praat met ons over een Stoeterij plan" },
      },
    },
    faqTitle: "Veelgestelde vragen over fokkerij software",
    faq: [
      { q: "Wat is de beste fokkerij software voor paarden?", a: "Voor 1 merrie zonder ingewikkelde fokmethode volstaan veel tools. Vanaf 2-3 merries, gebruik van KI of ET, of samenwerking met dierenarts en stamboek wordt een complete fokkerijmodule cruciaal. EquiSight combineert fokkerij met algemeen paardenbeheer in één app, waardoor je niet hoeft te switchen." },
      { q: "Welke fokkerij functies heb ik écht nodig?", a: "Minimaal: cyclus log, inseminatie/dekking gegevens, drachtcontrole-mijlpalen, en veulen dossier. Optioneel maar krachtig: stamboomweergave, hengstkeuze archief, DNA/paspoort en verkoop pipeline." },
      { q: "Kan EquiSight een merrie's cyclus voorspellen?", a: "Ja. EquiSight gebruikt eerdere cyclus-data om de voorspelde ovulatie te tonen, zodat je inseminatie of dekking op het juiste moment plant — zonder dagelijks puzzelen." },
      { q: "Werkt EquiSight ook voor embryotransplantatie?", a: "Ja. Donor- en ontvangermerrie krijgen gekoppelde dossiers, ET-kalender en gedeelde mijlpalen met de praktijk. Zo verlies je geen tijdlijn in de complexe ET-flow." },
      { q: "Kan ik mijn bestaande fokdata importeren?", a: "Je kunt cyclus, dekkingen en veulendata handmatig invoeren of in batch via CSV. Bij actieve fokkerijen helpen we met een eenmalige migratie als je over meerdere seizoenen data hebt staan." },
      { q: "Hoe deel ik dossier met mijn dierenarts of inseminator?", a: "Per persoon krijg je toegang met een rol: dierenarts, inseminator, hengstenhouder of stamboek-functionaris. Iedereen ziet alleen de informatie die nodig is voor zijn rol — geen volledige toegang nodig." },
    ],
  },

  manege: {
    seo: {
      title: "Manege software: complete vergelijking & keuzehulp 2026",
      description:
        "Vergelijk manege software voor lessen, ruiters, paarden en administratie. Ontdek welke app past bij jouw manege of pensionstal.",
      keywords: [
        "manege software",
        "stalmanagement app",
        "manege administratie software",
        "lesplanning manege",
        "pensionstal software",
        "ruiter administratie",
        "paarden administratie manege",
      ],
    },
    hero: {
      eyebrow: "Keuzehulp · manege software",
      breadcrumbLabel: "Manege software",
      title: "Manege software: de complete keuzehulp voor maneges en pensionstallen.",
      intro:
        "Lesplanning, ruiteradministratie, paardenbeheer, facturatie en communicatie — een goede manege software brengt het allemaal samen. Vergelijk de bekendste opties en ontdek welke past bij jouw stal of manege.",
      pills: [
        { icon: "Calendar", label: "Lesplanning" },
        { icon: "Users", label: "Ruiters & klanten" },
        { icon: "Heart", label: "Paardenzorg" },
        { icon: "CreditCard", label: "Facturatie" },
      ],
      ctas: [
        { label: "Vraag een demo aan", href: "#start", primary: true },
        { label: "Naar vergelijkingstabel", href: "#vergelijking" },
      ],
    },
    pains: {
      title: "Waarom verspreide tools een manege traag maken.",
      lead:
        "De meeste maneges draaien op een patchwork: lesplanning in een whiteboard, ruiterregistratie in Excel, facturatie in een tweede tool, paardenzorg in WhatsApp en eigenaarscontact via persoonlijke berichten. Bij een drukke week ontstaan dubbele boekingen, vergeten facturen of paarden die niet weten welke ruiter ze krijgen.",
      items: [
        "Wie staat er nu in les bij Jasmijn?",
        "Welke ruiter heeft nog niet betaald voor maart?",
        "Welk paard mag deze week niet ingezet worden?",
        "Wie zijn de noodcontacten van de leerlingen?",
        "Heeft de eigenaar van Stardust de hoefsmidfactuur al ontvangen?",
        "Welk paard staat aan welke box?",
      ],
    },
    what: {
      title: "Wat is manege software?",
      lead:
        "Manege software combineert lesplanning, ruiteradministratie, paardenbeheer, facturatie en communicatie in één tool. Geen Excel-acrobatiek of WhatsApp-spaghetti, maar één plek waar de manege werkt en eigenaren/ruiters meekijken.",
      targetGroupsTitle: "Voor welk type bedrijf is manege software bedoeld?",
      targetGroups: [
        { icon: "Building2", title: "Kleine manege (1-15 paarden)", body: "Vooral lesplanning en betalingen — administratie zonder hoofdpijn." },
        { icon: "Users", title: "Middelgrote manege (15-40 paarden)", body: "Meerdere instructeurs, lesplanning + paardrooster + ruiteradministratie." },
        { icon: "ClipboardList", title: "Grote rij- en pensionstal (40+)", body: "Volledige operatie inclusief facturatie, voer- en hoefsmidplanning." },
        { icon: "Heart", title: "Pensionstal", body: "Eigenaar-portaal, individuele zorginstructies, gedeelde agenda." },
        { icon: "Target", title: "Springruiter trainingstal", body: "Wedstrijdplanning, training per paard, klantcontact." },
        { icon: "FileText", title: "Hippische ondernemers", body: "Mix van les + pension + clinic + handel — alles vanuit één systeem." },
      ],
    },
    criteria: {
      title: "Waar moet goede manege software aan voldoen?",
      lead:
        "Een mooie app betekent niets als je medewerkers er niet mee willen werken. Vijf praktische criteria die het verschil maken tussen succes en frustratie.",
      items: [
        { icon: "Calendar", title: "Lesplanning op meerdere niveaus", body: "Beginner/gevorderd, dressuur/springen, paard-koppelingen en instructeur agenda's — allemaal naast elkaar zichtbaar." },
        { icon: "Users", title: "Ruiter administratie", body: "Profielen, niveau, abonnement, lessenkaart en familielidkoppelingen voor jeugdruiters." },
        { icon: "Heart", title: "Paardenrooster", body: "Welk paard wordt hoe vaak ingezet, met rust-, blessure- en menstruatieperiodes." },
        { icon: "CreditCard", title: "Facturatie & abonnementen", body: "Maandelijkse abonnementen, lessenkaarten, losse lessen en automatische incasso." },
        { icon: "Share2", title: "Eigenaarsportaal (pensionstal)", body: "Eigenaren zien hun paard in eigen app — agenda, kosten, hoefsmid en notities zonder bellen." },
        { icon: "Bell", title: "Communicatie & herinneringen", body: "Automatische lesherinneringen, betalingsmeldingen, hoefsmid-aankondigingen — zonder appen." },
      ],
    },
    comparison: {
      title: "EquiSight vs andere manege software.",
      lead:
        "Veel manege tools zijn sterk in lesplanning of facturatie, maar zelden in paardenzorg en eigenaarscommunicatie samen. We zetten ze eerlijk naast elkaar.",
      competitors: ["EquiSight", "Stalmanager", "Equino", "Manege App", "Spreadsheet"],
      rows: [
        { label: "Gratis starten / proefperiode",  values: [true,  true,  false, true,  true]  },
        { label: "Lesplanning",                    values: [true,  true,  true,  true,  false] },
        { label: "Meerdere instructeurs",          values: [true,  true,  true,  true,  false] },
        { label: "Ruiter administratie",           values: [true,  true,  true,  true,  true]  },
        { label: "Paardenrooster",                 values: [true,  true,  false, false, false] },
        { label: "Paardenzorg (vacc/hoefsmid)",    values: [true,  true,  false, false, false] },
        { label: "Lessenkaart / abonnement",       values: [true,  true,  true,  true,  false] },
        { label: "Online betalingen",              values: [true,  true,  true,  false, false] },
        { label: "Automatische facturatie",        values: [true,  true,  true,  false, false] },
        { label: "Eigenaarsportaal (pension)",     values: [true,  false, false, false, false] },
        { label: "Hoefsmid/dierenarts integratie", values: [true,  false, false, false, false] },
        { label: "WhatsApp/SMS herinneringen",     values: [true,  true,  false, false, false] },
        { label: "Mobiel (iOS/Android)",           values: [true,  true,  true,  true,  false] },
        { label: "Wedstrijdplanning",              values: [true,  false, false, false, false] },
        { label: "Geschikt voor pensionstal",      values: [true,  false, false, false, false] },
      ],
      note: "Vergelijking op basis van publiek beschikbare features in januari 2026. Stalmanager is sterk in administratie, EquiSight onderscheidt zich in pensionstal-flow + paardenzorg + eigenaars portaal.",
    },
    why: {
      title: "Waarom EquiSight anders is voor maneges en pensionstallen.",
      lead:
        "EquiSight gaat verder dan een lessplanner. We zien een manege als een complete operatie: paarden, mensen, communicatie en geld — allemaal afhankelijk van elkaar.",
      points: [
        { icon: "Heart", title: "Paard centraal, niet de agenda", body: "Elk paard heeft zijn eigen dossier. Lesinzet, gezondheid, hoefsmid en wedstrijden in één tijdlijn — niet 3 losse tools." },
        { icon: "Share2", title: "Eigenaars meekijken zonder bellen", body: "Pensionstal eigenaars hebben hun eigen view: kosten, planning, foto's en notities — bespaart 5-10 telefoontjes per week." },
        { icon: "Bell", title: "Automatische communicatie", body: "Lesherinnering, vacc-herinnering, factuur — automatisch, via app/email/SMS. Geen broadcast-WhatsApp meer." },
      ],
    },
    nicheB: {
      eyebrow: "Voor de pensionstal",
      title: "Wat een pensionstal van EquiSight krijgt.",
      lead: "Pensionstallen hebben unieke uitdagingen die normale manege software niet oplost.",
      cards: [
        { t: "Eigenaarsportaal", d: "Iedere eigenaar ziet alleen zijn paard(en), agenda en kosten." },
        { t: "Zorginstructies per paard", d: "Voer, ruwvoer, supplementen, dekenrooster — zichtbaar voor de stal." },
        { t: "Gedeelde agenda", d: "Hoefsmid, dierenarts en eigenaarsbezoek in één view." },
        { t: "Verbruikskosten per paard", d: "Strooisel, voer, krachtvoer doorbelast per eigenaar." },
        { t: "Noodcontacten paard", d: "Eigenaar + dierenarts + hoefsmid altijd vindbaar voor de stal." },
        { t: "Vacatiebeheer", d: "Welke paarden gaan op weidegang, op rust of in revalidatie?" },
      ],
    },
    beforeAfter: {
      title: "Van whiteboard + Excel naar EquiSight.",
      lead: "Concreet: zo verandert een werkweek op de manege.",
      rows: [
        { before: "Whiteboard met de lesplanning", after: "Live planning per instructeur" },
        { before: "Excel met ruiters + lesnotities", after: "Klant CRM met lessenhistorie" },
        { before: "WhatsApp aan eigenaren", after: "Eigenaars-portaal voor hun paard" },
        { before: "Maandelijkse incasso handmatig", after: "Automatische facturatie + iDEAL" },
        { before: "Paardzorg op een papierprikbord", after: "Zorginstructies per paard, gedeeld" },
        { before: "Mondelinge afspraak hoefsmid", after: "Hoefsmidplanning met bevestigingen" },
      ],
    },
    helper: {
      title: "Welke manege software past bij jou?",
      lead: "5 vragen om te zien of EquiSight het juiste platform is voor jouw manege of pensionstal.",
      questions: [
        { id: "size", q: "Hoeveel paarden heb je in je bedrijf?", options: [
          { label: "Minder dan 10", score: 1 },
          { label: "10-30", score: 2 },
          { label: "Meer dan 30", score: 3 },
        ] },
        { id: "type", q: "Wat is je hoofdactiviteit?", options: [
          { label: "Vooral lessen geven", score: 1 },
          { label: "Pensionstal (paarden in pension)", score: 3 },
          { label: "Mix van lessen + pension", score: 3 },
        ] },
        { id: "instructors", q: "Met hoeveel instructeurs werk je?", options: [
          { label: "Alleen ikzelf", score: 1 },
          { label: "2-3 instructeurs", score: 2 },
          { label: "4 of meer", score: 3 },
        ] },
        { id: "billing", q: "Hoe regel je je facturatie nu?", options: [
          { label: "Handmatig (Word/Excel)", score: 3 },
          { label: "Apart boekhoudprogramma", score: 2 },
          { label: "Geïntegreerd in lestool", score: 1 },
        ] },
        { id: "owners", q: "Hoe communiceer je met eigenaren?", options: [
          { label: "Persoonlijk / WhatsApp", score: 3 },
          { label: "Email + WhatsApp", score: 2 },
          { label: "Eigenaarsportaal in tool", score: 0 },
        ] },
      ],
      results: {
        low: { title: "Een lichte tool of Excel kan voorlopig — maar EquiSight schaalt mee.", body: "Voor een klein lesbedrijf met één instructeur en weinig pension is een uitgebreid pakket niet meteen nodig. EquiSight start gratis en groeit met je manege mee — geen migratie pijn over een paar jaar.", cta: "Start gratis" },
        mid: { title: "EquiSight is een logische upgrade voor jouw manege.", body: "Meerdere instructeurs, een mix van les en pension, of facturatie die nu losstaat — EquiSight bundelt dat in één werkstroom. Vooral het eigenaarsportaal scheelt veel ad-hoc communicatie.", cta: "Vraag een demo aan" },
        high: { title: "EquiSight is gebouwd voor jouw schaal.", body: "Pensionstal + lessen + meerdere instructeurs is precies waar EquiSight zich onderscheidt van klassieke manege tools. Het eigenaarsportaal en de paardenzorgintegratie sparen wekelijks uren administratie en communicatie.", cta: "Plan een demo voor jouw stal" },
      },
    },
    faqTitle: "Veelgestelde vragen over manege software",
    faq: [
      { q: "Wat is de beste manege software?", a: "Voor pure lesplanning zijn er meerdere goede opties. Vanaf het moment dat je een pensionstal, meerdere instructeurs of paardenzorg-tracking nodig hebt, wordt EquiSight de complete oplossing — geen tool-jongleren, alles op één plek." },
      { q: "Kan ik bestaande klanten en lessen importeren?", a: "Ja. Klantgegevens en lesschema's kun je via CSV importeren. We helpen actieve maneges met een eenmalige migratie zodat je niet 200 ruiters handmatig moet invoeren." },
      { q: "Werkt het ook voor een pensionstal zonder lessen?", a: "Ja. Pensionstal-modus schakelt lessen uit en focust op eigenaarsportaal, voer/zorg, hoefsmidplanning en kosten doorbelasten per paard." },
      { q: "Kunnen ruiters zelf inschrijven en betalen?", a: "Ja. Ruiters kunnen via een eigen account zien welke lessen open staan, zich inschrijven, lesgeld betalen met iDEAL en lessenkaarten kopen." },
      { q: "Hoe zit het met privacy van paard- en eigenaargegevens?", a: "Iedere persoon heeft een rol (eigenaar, ruiter, instructeur, medewerker). Per rol zie je alleen wat je nodig hebt. Eigenaren zien alleen hun paard, ruiters alleen hun eigen lessen." },
      { q: "Kan EquiSight koppelen aan mijn boekhoudsoftware?", a: "Ja, koppelingen met Exact, Moneybird en e-Boekhouden zijn beschikbaar. Facturen vloeien dan automatisch door naar je boekhouding." },
    ],
  },

  trainer: {
    seo: {
      title: "Trainer app paard: vergelijking & keuzehulp voor coaches 2026",
      description:
        "Vergelijk trainer apps voor paard en ruiter: trainingslog, video, doelen, protocolanalyse en klantbeheer. Vind de juiste tool voor coaches.",
      keywords: [
        "trainer app paard",
        "instructeur app",
        "paard training app",
        "ruiter coaching app",
        "trainingsdagboek paard",
        "dressuur protocol analyseren",
        "paardentrainer software",
      ],
    },
    hero: {
      eyebrow: "Keuzehulp · trainer software",
      breadcrumbLabel: "Trainer app",
      title: "Trainer app paard: complete keuzehulp voor instructeurs en coaches.",
      intro:
        "Een trainer of instructeur jongleert met combinaties, lessen, video's, doelen en wedstrijden. Vergelijk de beste trainer apps voor paardenmensen en kies het platform dat ook in 2026 nog meegroeit met jouw coaching.",
      pills: [
        { icon: "Target", label: "Doelen & planning" },
        { icon: "Video", label: "Video-analyse" },
        { icon: "TrendingUp", label: "Voortgang" },
        { icon: "Users", label: "Klanten/combinaties" },
      ],
      ctas: [
        { label: "Start gratis als trainer", href: "#start", primary: true },
        { label: "Naar vergelijkingstabel", href: "#vergelijking" },
      ],
    },
    pains: {
      title: "Waarom WhatsApp-coaching trainers in de weg zit.",
      lead:
        "De meeste trainers werken met losse tools: WhatsApp voor communicatie, een notitieboekje voor lessen, YouTube of WeTransfer voor video's en Excel voor wedstrijdresultaten. Bij 5 ruiters werkt het. Bij 20 ruiters gaat informatie verloren, vergeet je doelen en verlies je grip op individuele progressie.",
      items: [
        "Waar staat het doel van Anouk voor dit seizoen?",
        "Welke video heb ik vorige maand naar Bram gestuurd?",
        "Heeft Lara de protocolanalyse van haar laatste wedstrijd gezien?",
        "Welke combinatie traint hoe vaak per week?",
        "Heeft de eigenaar van Stardust m'n training-instructie gelezen?",
        "Wanneer was de laatste keer dat ik feedback gaf op fluctuerende impuls?",
      ],
    },
    what: {
      title: "Wat is een trainer app voor paard en ruiter?",
      lead:
        "Een trainer app combineert een coachings-CRM (combinaties, lessen, doelen), een trainingslog per paard, video-feedback, wedstrijdplanning en protocolanalyse. Geen losse WhatsApp/YouTube/Excel meer, maar één platform waar trainer en ruiter samen werken.",
      targetGroupsTitle: "Voor welk type trainer is dit bedoeld?",
      targetGroups: [
        { icon: "Target", title: "Dressuurinstructeur", body: "Protocol scannen, jurycommentaar koppelen aan trainingsdoelen, progressie zien." },
        { icon: "Activity", title: "Springtrainer", body: "Parcoursplanning, rondefeedback, hoogtebeheer per paard." },
        { icon: "Award", title: "Eventing coach", body: "Drie disciplines, drie planning - allemaal naast elkaar zichtbaar." },
        { icon: "TrendingUp", title: "Performance coach (sport)", body: "EPI-tracking, wedstrijden, doelen — voor topsporters en hun teams." },
        { icon: "Users", title: "Manege-instructeur", body: "Groepslessen + individuele coaching in één app." },
        { icon: "Heart", title: "Recreatieve coach", body: "Hippische therapie, beginners, eigenaren met één paard." },
      ],
    },
    criteria: {
      title: "Waar moet een goede trainer app aan voldoen?",
      lead:
        "Trainen is mensenwerk, maar de juiste tool maakt je 30-50% efficiënter en helpt ruiters ook tussen lessen door beter te worden.",
      items: [
        { icon: "Users", title: "Combinaties beheer", body: "Paard + ruiter als één unit, met niveau, doelen, voortgang en historie." },
        { icon: "ClipboardList", title: "Lesnotities & doelen", body: "Per les een notitie met focuspunten, gekoppeld aan langere termijn doelen." },
        { icon: "Video", title: "Video feedback", body: "Upload video, tag momenten, voeg gesproken of geschreven feedback toe." },
        { icon: "Target", title: "Wedstrijd & protocol analyse", body: "Scan protocollen (KNHS/FEI), zie scores per onderdeel, jurycommentaar en trends." },
        { icon: "Calendar", title: "Planning & lessenkaarten", body: "Agenda, betalingen, abonnementen en automatische herinneringen." },
        { icon: "Share2", title: "Communicatie naar ruiter", body: "Ruiter ziet zijn eigen pagina met lessen, video's, doelen en feedback." },
      ],
    },
    comparison: {
      title: "EquiSight vs andere trainer apps.",
      lead:
        "Sommige trainer tools focussen op WhatsApp-vervanging, andere op protocol analyse. EquiSight combineert beide met een echt paardendossier — zodat de ruiter ook tussen lessen door beter wordt.",
      competitors: ["EquiSight", "Coach Apps (algemeen)", "HorseTrainer Pro", "EquiPilot", "WhatsApp + Excel"],
      rows: [
        { label: "Gratis starten",                  values: [true,  true,  false, true,  true]  },
        { label: "Combinaties beheer",              values: [true,  true,  true,  true,  false] },
        { label: "Lesnotities & feedback",          values: [true,  true,  true,  true,  true]  },
        { label: "Doelen op langere termijn",       values: [true,  true,  true,  true,  false] },
        { label: "Video upload + feedback",         values: [true,  true,  true,  false, false] },
        { label: "KNHS/FEI protocol scannen",       values: [true,  false, false, false, false] },
        { label: "EPI (Performance Index)",         values: [true,  false, false, false, false] },
        { label: "Wedstrijdkalender",               values: [true,  false, true,  true,  false] },
        { label: "Ruiter portaal",                  values: [true,  true,  false, true,  false] },
        { label: "Lessenkaart / abonnement",        values: [true,  false, false, true,  false] },
        { label: "Online betalingen",               values: [true,  false, false, true,  false] },
        { label: "Geïntegreerd paardendossier",     values: [true,  false, false, false, false] },
        { label: "Hoefsmid/dierenarts zichtbaar",   values: [true,  false, false, false, false] },
        { label: "Voor instructeur EN ruiter",      values: [true,  true,  false, true,  true]  },
        { label: "Multi-paard per ruiter",          values: [true,  true,  true,  true,  false] },
      ],
      note: "Vergelijking op basis van publiek beschikbare features in januari 2026. EquiSight is uniek in protocol analyse + EPI én combineert dit met algemeen paardenbeheer.",
    },
    why: {
      title: "Waarom EquiSight een andere trainer ervaring biedt.",
      lead:
        "De meeste trainer tools eindigen waar de les ophoudt. EquiSight blijft doorlopen — in het paarddossier, in de wedstrijdanalyse, in de samenwerking met dierenarts en eigenaar.",
      points: [
        { icon: "TrendingUp", title: "Protocol scan → trainingsdoel", body: "Scan een KNHS-protocol en zie direct welke onderdelen aandacht behoeven. Doelen komen automatisch naar voren." },
        { icon: "Sparkles", title: "EquiCoach versterkt je oog", body: "Data van meerdere lessen, wedstrijden en protocols laat patronen zien die je in je hoofd niet houdt." },
        { icon: "Share2", title: "Geïntegreerd met het paard", body: "Trainer ziet vacc, hoefsmid en blessure-historie van het paard — geen training plannen tijdens herstel." },
      ],
    },
    nicheA: {
      eyebrow: "Voor dressuur en springen",
      title: "Wat een coaching dossier in EquiSight bevat.",
      lead: "Per combinatie krijg je een coaching-stream die alles bij elkaar houdt — niet meer dingen kwijtraken in WhatsApp.",
      items: [
        "Lesnotities met focuspunten per sessie",
        "Lange termijn doelen + mijlpalen",
        "Video upload met tijdcode-comments",
        "KNHS/FEI protocol scan met scores",
        "Jurycommentaar gekoppeld aan training",
        "Wedstrijdresultaten en EPI trend",
        "Klantcontact en lessenhistorie",
        "Lessenkaart / abonnement / facturen",
        "Voortgangsgrafiek per discipline",
        "Gedeelde toegang met paardeigenaar",
      ],
      ctaLabel: "Bekijk de trainermodule",
      ctaHref: "/trainer",
    },
    beforeAfter: {
      title: "Van WhatsApp + YouTube naar EquiSight Trainer.",
      lead: "Concreet: zo verandert je coaching workflow.",
      rows: [
        { before: "WhatsApp met losse video links", after: "Video met tijdcode-feedback in dossier" },
        { before: "Notitieboekje per ruiter", after: "Coaching log met doelen + history" },
        { before: "Excel met wedstrijdscores", after: "EPI grafiek + protocol analyse" },
        { before: "Mondelinge doelen", after: "Mijlpalen met progressie indicator" },
        { before: "Apart facturatie tooltje", after: "Lessen + iDEAL + abonnement in app" },
        { before: "Mailtje aan eigenaar over voortgang", after: "Eigenaar volgt in eigen view" },
      ],
    },
    helper: {
      title: "Welke trainer app past bij jou?",
      lead: "5 vragen om te zien of een gespecialiseerde coaching tool meerwaarde heeft voor jouw praktijk.",
      questions: [
        { id: "clients", q: "Hoeveel actieve combinaties coach je?", options: [
          { label: "Minder dan 5", score: 1 },
          { label: "5-15", score: 2 },
          { label: "Meer dan 15", score: 3 },
        ] },
        { id: "discipline", q: "Wat is je hoofd discipline?", options: [
          { label: "Dressuur", score: 3 },
          { label: "Springen", score: 2 },
          { label: "Recreatief / divers", score: 1 },
        ] },
        { id: "video", q: "Gebruik je video bij coaching?", options: [
          { label: "Zelden", score: 0 },
          { label: "Soms (handmatig)", score: 2 },
          { label: "Vaak — structureel", score: 3 },
        ] },
        { id: "comp", q: "Coach je wedstrijdruiters?", options: [
          { label: "Nee, recreatief", score: 0 },
          { label: "Soms wedstrijden", score: 2 },
          { label: "Veel — startkaarten centraal", score: 3 },
        ] },
        { id: "depth", q: "Wil je je coaching meten?", options: [
          { label: "Nee, intuïtie volstaat", score: 0 },
          { label: "Wel, maar geen tijd", score: 2 },
          { label: "Ja — data-driven coaching", score: 3 },
        ] },
      ],
      results: {
        low: { title: "Een eenvoudige tool of zelfs WhatsApp volstaat — voor nu.", body: "Voor minder dan 5 ruiters zonder veel video of wedstrijden is een coaching app overkill. EquiSight start gratis, dus je kunt het uitproberen wanneer je praktijk groeit.", cta: "Start gratis" },
        mid: { title: "EquiSight Trainer maakt je structureel efficiënter.", body: "Met 5-15 combinaties verlies je per maand uren aan WhatsApp en losse video's. EquiSight bundelt alles, scant protocollen automatisch en zorgt dat ruiters hun feedback altijd kunnen terugzien.", cta: "Probeer Trainer module" },
        high: { title: "EquiSight is gebouwd voor jouw coaching schaal.", body: "Veel ruiters, video, wedstrijden en data-driven coaching: dit is precies waar EquiSight zich onderscheidt. EPI + protocolscan + paardendossier geeft je een voorsprong die concurrenten niet hebben.", cta: "Plan een demo voor je coaching praktijk" },
      },
    },
    faqTitle: "Veelgestelde vragen over trainer apps",
    faq: [
      { q: "Wat is de beste trainer app voor paard en ruiter?", a: "Voor pure communicatie volstaan veel tools (zelfs WhatsApp). Voor doelen, video-analyse, protocol scannen, wedstrijdtracking en klantbeheer in één tool is EquiSight de meest complete oplossing — vooral omdat het ook het paard zelf bijhoudt, niet alleen de combinatie." },
      { q: "Kan EquiSight wedstrijdprotocollen automatisch lezen?", a: "Ja. Upload een KNHS- of FEI-protocol en EquiSight extraheert de scores per onderdeel, jurycommentaren en koppelt dit aan de combinatie. Trends zie je over meerdere wedstrijden heen." },
      { q: "Wat is de EPI (Equestrian Performance Index)?", a: "EPI is een EquiSight-score die meerdere wedstrijdresultaten omzet naar een vergelijkbare prestatie-index. Daardoor zie je echte voortgang door verschillende klassen en jurys heen." },
      { q: "Kunnen ruiters mijn lessen zelf inboeken en betalen?", a: "Ja. Ruiters loggen in, zien beschikbare lestijden, kopen lessenkaarten en betalen via iDEAL. Jij keurt af of toe wat je wilt aanbieden." },
      { q: "Werkt het ook voor een instructeur die rond de manege werkt?", a: "Ja. Je kunt als freelance trainer met eigen klanten werken zonder een manege account te koppelen. Klanten betalen je direct." },
      { q: "Kan ik bestaande video's en notities migreren?", a: "Video's kun je per combinatie uploaden of via een batch-upload tool. Notities import via CSV. We helpen actieve trainers met een eenmalige migratie." },
    ],
  },

  gidsen: {
    seo: {
      title: "Gidsen & keuzehulpen voor paardenbeheer software | EquiSight",
      description:
        "Alle EquiSight gidsen op één plek: paardenbeheer, fokkerij, manege en trainer software vergelijkingen plus keuzehulpen voor 2026.",
      keywords: [
        "paardensoftware gidsen",
        "paardenbeheer keuzehulp",
        "fokkerij software vergelijken",
        "manege software gids",
        "trainer app vergelijking",
      ],
    },
    hub: {
      eyebrow: "Hub · gidsen",
      breadcrumbLabel: "Gidsen",
      title: "Vier complete gidsen om de juiste paardensoftware te kiezen.",
      intro:
        "Voor iedere doelgroep een diepgaande keuzehulp: vergelijkingstabellen, criteria, voorbeelden en interactieve quiz. Klik door naar de gids die past bij jouw situatie, of verken eerst de verdiepende artikelen onder elke cluster.",
      stats: {
        guides: "4 keuzehulpen",
        articles: "15 verdiepende artikelen",
        fair: "Eerlijke vergelijking",
      },
      readMoreCta: "Lees de complete gids",
      relatedHeading: "Verdiepende artikelen",
      articlesCountSingular: "{{count}} verdiepend artikel",
      articlesCountPlural: "{{count}} verdiepende artikelen",
      articlesCountAria: "Aantal cluster-artikelen",
      lastUpdatedPrefix: "Bijgewerkt",
      lastUpdatedAria: "Datum laatste update van deze cluster",
    },
    clusters: [
      {
        path: "/beste-paardenbeheer-app",
        icon: "Compass",
        title: "Paardenbeheer app keuzehulp",
        blurb:
          "Vergelijk de bekendste paardenbeheer apps en ontdek welke past bij eigenaar, stal of fokkerij. Inclusief eerlijke vergelijkingstabel en 5-vragen keuzehulp.",
        audience: "Voor eigenaren, stalhouders en alle paardenmensen die overzicht zoeken.",
      },
      {
        path: "/fokkerij-software-paard",
        icon: "Baby",
        title: "Fokkerij software vergelijking",
        blurb:
          "Van merriecyclus tot veulenontwikkeling: bekijk welke fokkerij software echt werkt voor jouw aantal merries, fokmethode en stamboek-eisen.",
        audience: "Voor (hobby)fokkers, stoeterijen en hengstenhouders.",
      },
      {
        path: "/manege-software",
        icon: "Building2",
        title: "Manege software keuzehulp",
        blurb:
          "Lesplanning, ruiteradministratie, eigenaarsportaal en facturatie — in één tool. Vergelijk de opties voor maneges en pensionstallen in 2026.",
        audience: "Voor maneges, pensionstallen en rij-bedrijven.",
      },
      {
        path: "/trainer-app-paard",
        icon: "Target",
        title: "Trainer app keuzehulp",
        blurb:
          "Coaching-CRM, video-feedback, KNHS-protocol scannen en doelen per combinatie. Welke trainer app past bij jouw coaching praktijk?",
        audience: "Voor instructeurs, trainers en coaches.",
      },
    ],
    closing: {
      eyebrow: "Waarom deze gidsen",
      title: "Eerlijk vergelijken, niet alleen prijzen.",
      body1:
        "Veel \"vergelijkingspagina's\" zijn verkapte advertenties. Onze gidsen zetten functies, doelgroep en concurrenten naast elkaar zoals een onafhankelijke kennisbron dat zou doen. EquiSight komt natuurlijk naar voren als complete oplossing, maar je leest ook eerlijk waar andere apps sterker zijn.",
      body2:
        "De verdiepende artikelen onder elke gids beantwoorden de concrete vragen die voortkomen uit de keuze: \"Wanneer eerste echo na inseminatie\", \"Hoe automatiseer ik manege facturatie\", \"Hoe analyseer ik een wedstrijdprotocol\". Geen marketingfluff, wel praktijk.",
    },
    hubName: "EquiSight Gidsen",
  },

  // Reusable UI chrome strings shared by CornerstonePage
  ui: {
    sectionEyebrows: {
      pains: "Waarom dit pijn doet",
      basics: "Basics",
      criteria: "Wat maakt het goed?",
      comparison: "Eerlijke vergelijking",
      why: "Het verschil",
      beforeAfter: "Van versnippering naar overzicht",
      helper: "5 vragen",
    },
    table: {
      feature: "Functie",
      today: "Vandaag",
      withEquisight: "Met EquiSight",
    },
    helper: {
      questionLabel: "Vraag {{current}} van {{total}}",
      answerPrompt: "Beantwoord de {{count}} resterende vraag om een aanbeveling te zien.",
      answerPromptPlural: "Beantwoord de {{count}} resterende vragen om een aanbeveling te zien.",
      resultEyebrow: "Jouw resultaat",
    },
    targetGroupsFallback: "Voor wie is dit bedoeld?",
    faqTitleFallback: "Veelgestelde vragen",
  },
};

export default CORNERSTONES_NL;
